import React, { useState, useEffect } from "react";
import { Edit } from "lucide-react";
import "./ProductDetails.css";
import BaseButton from "../Common/BaseButton";
import Button from 'react-bootstrap/Button';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const MetafieldSection = (props) => {
  const { 
    metafield, 
    handleProductRegenerate, 
    previewMode, 
    loading, 
    handleManualEdit, 
    metafields, 
    setMetafields,
    onHide,
    sectionId,
    sectionTitle,
    isHidden,
    onUnhide
  } = props;
 const getInitialValue = () => {
    if (metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0) {
      return JSON.stringify(metafield.metaobjects, null, 2);
    }
    return metafield.value || '';
  };

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(getInitialValue());
  const [previousResponse, setPreviousResponse] = useState(metafield.value || "");
  const [editableMetaobjects, setEditableMetaobjects] = useState(
    metafield.metaobjects && Array.isArray(metafield.metaobjects) 
      ? JSON.parse(JSON.stringify(metafield.metaobjects)) 
      : []
  );
  useEffect(() => {
    if (!isEditing) {
      if (metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0) {
        setValue(JSON.stringify(metafield.metaobjects, null, 2));
        setEditableMetaobjects(JSON.parse(JSON.stringify(metafield.metaobjects)));
      } else {
        setValue(metafield.value || '');
      }
    }
  }, [metafield.metaobjects, metafield.value, isEditing]);

  // Function to strip HTML tags and get plain text, ignoring style tags completely
  const stripHtmlTags = (html) => { 
    if (typeof html !== 'string') return String(html || ''); 
    
    // First remove style tags and their content completely
    let cleanedHtml = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    
    // Then remove all remaining HTML tags
    cleanedHtml = cleanedHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    
    return cleanedHtml;
  };

  // Function to check if content has HTML tags
  const hasHtmlTags = (content) => {
    if (typeof content !== 'string') return false;
    return /<[^>]*>/g.test(content);
  };

  useEffect(() => {
    if (value && typeof value === 'string' && value.trim() !== "") {
      setPreviousResponse(value);
    } else if (value && typeof value !== 'string') {
      // Handle non-string values
      setPreviousResponse(value);
    }
  }, [value]);

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleDoneClick = () => {
    setIsEditing(false);
    
    if (metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0) {
      const updatedMetafields = metafields.map(m => 
        m.id === metafield.id 
          ? { ...m, metaobjects: editableMetaobjects }
          : m
      );
      setMetafields(updatedMetafields);
    } else {
      const updatedMetafields = metafields.map(m => 
        m.id === metafield.id 
          ? { ...m, value: value }
          : m
      );
      setMetafields(updatedMetafields);
    }
  };
  
  // Function to update a metaobject property
  const updateMetaobjectProperty = (metaIndex, key, newValue) => {
    const updated = [...editableMetaobjects];
    if (updated[metaIndex]) {
      updated[metaIndex] = { ...updated[metaIndex], [key]: newValue };
      setEditableMetaobjects(updated);
    }
  };
  
  const updateNestedArrayProperty = (metaIndex, arrayKey, itemIndex, itemKey, newValue) => {
    const updated = [...editableMetaobjects];
    if (updated[metaIndex] && updated[metaIndex][arrayKey] && Array.isArray(updated[metaIndex][arrayKey])) {
      const arrayCopy = [...updated[metaIndex][arrayKey]];
      if (arrayCopy[itemIndex]) {
        arrayCopy[itemIndex] = { ...arrayCopy[itemIndex], [itemKey]: newValue };
        updated[metaIndex] = { ...updated[metaIndex], [arrayKey]: arrayCopy };
        setEditableMetaobjects(updated);
      }
    }
  };

  const handleRegenerate = async (type, formData) => {
    let furtherDetailsToSend = "";
    let customPromptToSend = null;

    if (typeof formData === "object" && formData !== null) {
      if (formData.reason && typeof formData.reason === 'string' && formData.reason.trim() !== "") {
        furtherDetailsToSend = formData.reason;
        customPromptToSend = null;
      } else {
        customPromptToSend = {
          goal: formData.goal,
          role: formData.role,
          constraints: formData.constraints,
        };
      }
    } else if (typeof formData === "string") {
      furtherDetailsToSend = formData;
    }

    // For metafield regeneration, we'll regenerate the specific metafield
    await handleProductRegenerate("metafield", furtherDetailsToSend, metafield, customPromptToSend, previousResponse);
  };

  const formatKey = (key) => {
    return key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Function to convert Shopify rich text JSON to HTML string
  const convertRichTextToHtml = (richTextData) => {
    if (!richTextData) return '';
    
    // Parse if it's a string
    let parsed;
    if (typeof richTextData === 'string') {
      try {
        parsed = JSON.parse(richTextData);
      } catch (e) {
        return richTextData; // Return as-is if not valid JSON
      }
    } else {
      parsed = richTextData;
    }
    
    // Check if it's a rich text structure
    if (parsed.type === 'root' && parsed.children) {
      return convertRichTextNodesToHtml(parsed.children);
    }
    
    return '';
  };

  // Recursive function to convert rich text nodes to HTML
  const convertRichTextNodesToHtml = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) return '';
    
    return nodes.map((node, index) => {
      if (!node) return '';
      
      switch (node.type) {
        case 'heading':
          const level = node.level || 2;
          const headingContent = node.children ? convertRichTextNodesToHtml(node.children) : '';
          return `<h${level}>${headingContent}</h${level}>`;
          
        case 'paragraph':
          const paraContent = node.children ? convertRichTextNodesToHtml(node.children) : '';
          return `<p>${paraContent}</p>`;
          
        case 'text':
          let textContent = node.value || '';
          if (node.bold) textContent = `<strong>${textContent}</strong>`;
          if (node.italic) textContent = `<em>${textContent}</em>`;
          if (node.underline) textContent = `<u>${textContent}</u>`;
          return textContent;
          
        case 'link':
          const linkContent = node.children ? convertRichTextNodesToHtml(node.children) : '';
          const target = node.target || '_blank';
          return `<a href="${node.url || '#'}" target="${target}" rel="noopener noreferrer">${linkContent}</a>`;
          
        case 'list':
          const listTag = node.listType === 'ordered' ? 'ol' : 'ul';
          const listContent = node.children ? convertRichTextNodesToHtml(node.children) : '';
          return `<${listTag}>${listContent}</${listTag}>`;
          
        case 'list-item':
        case 'listItem':
          const itemContent = node.children ? convertRichTextNodesToHtml(node.children) : '';
          return `<li>${itemContent}</li>`;
          
        default:
          return '';
      }
    }).join('');
  };

  const renderValue = (value) => {
    // Check if this is a rich text field
    if (metafield.type === 'rich_text_field' || metafield.type === 'rich_text') {
      const htmlContent = convertRichTextToHtml(value);
      if (htmlContent) {
        return (
          <div 
            className="metafield-html-content"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );
      }
    }
    
    // Convert value to string for processing
    let stringValue;
    if (typeof value === 'string') {
      stringValue = value;
    } else if (typeof value === 'number') {
      stringValue = value.toString();
    } else if (Array.isArray(value)) {
      stringValue = value.join(', ');
    } else if (typeof value === 'object') {
      stringValue = JSON.stringify(value, null, 2);
    } else {
      stringValue = String(value);
    }
    
    // Check if the value contains HTML tags
    const hasHtmlTags = /<[^>]*>/g.test(stringValue);
    
    if (hasHtmlTags) {
      // For HTML content, render as HTML
      return (
        <div 
          className="metafield-html-content"
          dangerouslySetInnerHTML={{ __html: stringValue }}
        />
      );
    } else {
      // For plain text, render as is
      return (
        <div className="metafield-text-content">
          {stringValue}
        </div>
      );
    }
  };

  const renderMetafieldValue = (fieldValue, fieldType = null) => {
    // Check if this is a rich text field value
    if (fieldType === 'rich_text_field' || fieldType === 'rich_text') {
      const htmlContent = convertRichTextToHtml(fieldValue);
      if (htmlContent) {
        return (
          <div 
            className="metafield-html-content"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );
      }
    }
    
    // Also check if the value itself is a rich text structure
    if (typeof fieldValue === 'string') {
      try {
        const parsed = JSON.parse(fieldValue);
        if (parsed.type === 'root' && parsed.children) {
          const htmlContent = convertRichTextToHtml(parsed);
          if (htmlContent) {
            return (
              <div 
                className="metafield-html-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            );
          }
        }
      } catch (e) {
        // Not JSON, continue with normal processing
      }
    } else if (typeof fieldValue === 'object' && fieldValue !== null && fieldValue.type === 'root') {
      const htmlContent = convertRichTextToHtml(fieldValue);
      if (htmlContent) {
        return (
          <div 
            className="metafield-html-content"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );
      }
    }
    
    // Convert value to string for processing
    let stringValue;
    if (typeof fieldValue === 'string') {
      stringValue = fieldValue;
    } else if (typeof fieldValue === 'number') {
      stringValue = fieldValue.toString();
    } else if (Array.isArray(fieldValue)) {
      stringValue = fieldValue.join(', ');
    } else if (typeof fieldValue === 'object') {
      stringValue = JSON.stringify(fieldValue, null, 2);
    } else {
      stringValue = String(fieldValue);
    }
    
    // Check if the value contains HTML tags
    const hasHtmlTags = /<[^>]*>/g.test(stringValue);
    
    if (hasHtmlTags) {
      // For HTML content, render as HTML
      return (
        <div 
          className="metafield-html-content"
          dangerouslySetInnerHTML={{ __html: stringValue }}
        />
      );
    } else {
      // For plain text, render as is
      return (
        <div className="metafield-text-content">
          {stringValue}
        </div>
      );
    }
  };

  const renderMetaobjects = () => {
    if (!metafield.metaobjects || !Array.isArray(metafield.metaobjects) || metafield.metaobjects.length === 0) {
      return null;
    }

    return (
      <div className="metaobjects-container">
        {metafield.metaobjects.map((metaobject, metaIndex) => {
          const metaobjectType = metaobject.type || '';
          
          // Render all properties of the metaobject dynamically
          const renderMetaobjectProperties = () => {
            // Get all keys except system keys
            const propertyKeys = Object.keys(metaobject).filter(key => 
              !['id', 'type', 'handle'].includes(key)
            );
            
            if (propertyKeys.length === 0) {
              return null;
            }
            
            return (
              <div style={{ 
                background: '#ffffff', 
                padding: '12px', 
                borderRadius: '4px', 
                border: '1px solid #e5e7eb',
                marginTop: '10px'
              }}>
                {propertyKeys.map(key => {
                  const value = metaobject[key];
                  
                  // Skip null/undefined values
                  if (value === null || value === undefined) {
                    return null;
                  }
                  
                  // Handle arrays (like herbs_list)
                  if (Array.isArray(value)) {
                    if (value.length === 0) {
                      return null;
                    }
                    
                    return (
                      <div key={key} style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 600, color: '#495057', marginBottom: '8px' }}>
                          {formatKey(key)}:
                        </div>
                        {value.map((item, itemIndex) => {
                          if (typeof item === 'object' && item !== null) {
                            return (
                              <div key={itemIndex} style={{ 
                                marginBottom: '10px', 
                                padding: '10px', 
                                background: '#f8f9fa', 
                                borderRadius: '4px',
                                border: '1px solid #e9ecef',
                                marginLeft: '10px'
                              }}>
                                {Object.keys(item).map(itemKey => {
                                  if (item[itemKey] !== null && item[itemKey] !== undefined) {
                                    return (
                                      <div key={itemKey} style={{ marginBottom: '8px' }}>
                                        <div style={{ fontWeight: 600, color: '#495057', marginBottom: '4px', fontSize: '14px' }}>
                                          {formatKey(itemKey)}:
                                        </div>
                                        <div style={{ color: '#6c757d', fontSize: '14px' }}>
                                          {renderMetafieldValue(item[itemKey])}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            );
                          } else {
                            return (
                              <div key={itemIndex} style={{ 
                                marginBottom: '6px', 
                                marginLeft: '10px',
                                color: '#6c757d'
                              }}>
                                {renderMetafieldValue(item)}
                              </div>
                            );
                          }
                        })}
                      </div>
                    );
                  }
                  
                  // Handle objects
                  if (typeof value === 'object' && value !== null) {
                    return (
                      <div key={key} style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 600, color: '#495057', marginBottom: '8px' }}>
                          {formatKey(key)}:
                        </div>
                        <div style={{ 
                          padding: '10px', 
                          background: '#f8f9fa', 
                          borderRadius: '4px',
                          border: '1px solid #e9ecef',
                          marginLeft: '10px'
                        }}>
                          {Object.keys(value).map(subKey => {
                            if (value[subKey] !== null && value[subKey] !== undefined) {
                              return (
                                <div key={subKey} style={{ marginBottom: '8px' }}>
                                  <div style={{ fontWeight: 600, color: '#495057', marginBottom: '4px', fontSize: '14px' }}>
                                    {formatKey(subKey)}:
                                  </div>
                                  <div style={{ color: '#6c757d', fontSize: '14px' }}>
                                    {renderMetafieldValue(value[subKey])}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    );
                  }
                  
                  // Handle primitive values (string, number, boolean)
                  return (
                    <div key={key} style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: 600, color: '#495057', marginBottom: '4px' }}>
                        {formatKey(key)}:
                      </div>
                      <div style={{ color: '#6c757d' }}>
                        {renderMetafieldValue(value, key === 'answer' ? 'rich_text_field' : null)}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          };

          return (
            <div key={metaobject.id || metaIndex} className="metaobject-item" style={{
              marginBottom: '20px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '5px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ marginBottom: '10px' }}>
                <strong style={{ color: '#495057' }}>Type: {metaobjectType || 'N/A'}</strong>
                {metaobject.handle && (
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                    Handle: {metaobject.handle}
                  </div>
                )}
              </div>
              
              {/* Render all properties dynamically */}
              {renderMetaobjectProperties()}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="product-section">
      <div className="section-header">
        <div className="section-title">{formatKey(metafield.key)}</div>
        {!previewMode && (
          isEditing ? (
            <Button variant="dark" onClick={handleDoneClick}>Done</Button>
          ) : (
            <div className="button-group">
              <BaseButton
                className="outlineprimary"
                children="Regenerate Text"
                imageSrc="/regenerate-text.svg"
                isModalOpen={true}
                handleClick={(formData) => handleRegenerate("metafield", formData)}
              />
              {isHidden && onUnhide ? (
                <BaseButton
                  className="outlineprimary"
                  children="Unhide Section"
                  handleClick={onUnhide}
                />
              ) : onHide ? (
                <BaseButton
                  className="outlineprimary"
                  children="Hide Section"
                  handleClick={() => onHide(sectionId, sectionTitle)}
                />
              ) : null}
              <BaseButton
                className="outlineprimary btn-sm"
                children="Edit Text"
                imageSrc="/edit-text.svg"
                handleClick={toggleEdit}
              />
            </div>
          )
        )}
      </div>

      {loading ? (
        <div>
          <Skeleton height={12} width="90%" style={{ marginBottom: 5 }} count={5} />
        </div>
      ) : isEditing ? (
        metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0 ? (
          <div className="metaobjects-edit-container" style={{ padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
            {editableMetaobjects.map((metaobject, metaIndex) => {
              const metaobjectType = metaobject.type || '';
              const propertyKeys = Object.keys(metaobject).filter(key => !['id', 'type', 'handle'].includes(key));
              
              return (
                <div key={metaIndex} style={{ 
                  marginBottom: '20px', 
                  padding: '15px', 
                  background: '#ffffff', 
                  borderRadius: '5px', 
                  border: '1px solid #dee2e6' 
                }}>
                  <div style={{ marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid #dee2e6' }}>
                    <strong style={{ color: '#495057' }}>Type: {metaobjectType || 'N/A'}</strong>
                    {metaobject.handle && (
                      <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                        Handle: {metaobject.handle}
                      </div>
                    )}
                  </div>
                  
                  {propertyKeys.map(key => {
                    const propValue = metaobject[key];
                    
                    if (propValue === null || propValue === undefined) {
                      return null;
                    }
                    
                    // Handle arrays (like herbs_list)
                    if (Array.isArray(propValue)) {
                      if (propValue.length === 0) {
                        return null;
                      }
                      
                      return (
                        <div key={key} style={{ marginBottom: '15px' }}>
                          <label style={{ fontWeight: 600, color: '#495057', marginBottom: '8px', display: 'block' }}>
                            {formatKey(key)}:
                          </label>
                          {propValue.map((item, itemIndex) => {
                            if (typeof item === 'object' && item !== null) {
                              return (
                                <div key={itemIndex} style={{ 
                                  marginBottom: '10px', 
                                  padding: '10px', 
                                  background: '#f8f9fa', 
                                  borderRadius: '4px',
                                  border: '1px solid #e9ecef',
                                  marginLeft: '10px'
                                }}>
                                  {Object.keys(item).map(itemKey => {
                                    if (item[itemKey] !== null && item[itemKey] !== undefined) {
                                      return (
                                        <div key={itemKey} style={{ marginBottom: '8px' }}>
                                          <label style={{ fontWeight: 600, color: '#495057', marginBottom: '4px', fontSize: '14px', display: 'block' }}>
                                            {formatKey(itemKey)}:
                                          </label>
                                          <textarea
                                            value={typeof item[itemKey] === 'string' ? item[itemKey] : JSON.stringify(item[itemKey])}
                                            onChange={(e) => updateNestedArrayProperty(metaIndex, key, itemIndex, itemKey, e.target.value)}
                                            className="form-control"
                                            rows={3}
                                            style={{ fontSize: '14px' }}
                                          />
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              );
                            } else {
                              return (
                                <div key={itemIndex} style={{ marginLeft: '10px', marginBottom: '8px' }}>
                                  <textarea
                                    value={String(item)}
                                    onChange={(e) => {
                                      const updated = [...editableMetaobjects];
                                      if (updated[metaIndex] && updated[metaIndex][key]) {
                                        const arrayCopy = [...updated[metaIndex][key]];
                                        arrayCopy[itemIndex] = e.target.value;
                                        updated[metaIndex] = { ...updated[metaIndex], [key]: arrayCopy };
                                        setEditableMetaobjects(updated);
                                      }
                                    }}
                                    className="form-control"
                                    rows={2}
                                    style={{ fontSize: '14px' }}
                                  />
                                </div>
                              );
                            }
                          })}
                        </div>
                      );
                    }
                    
                    // Handle objects
                    if (typeof propValue === 'object' && propValue !== null) {
                      return (
                        <div key={key} style={{ marginBottom: '15px' }}>
                          <label style={{ fontWeight: 600, color: '#495057', marginBottom: '8px', display: 'block' }}>
                            {formatKey(key)}:
                          </label>
                          <textarea
                            value={JSON.stringify(propValue, null, 2)}
                            onChange={(e) => {
                              try {
                                const parsed = JSON.parse(e.target.value);
                                updateMetaobjectProperty(metaIndex, key, parsed);
                              } catch (e) {
                                // Invalid JSON, don't update
                              }
                            }}
                            className="form-control"
                            rows={5}
                            style={{ fontFamily: 'monospace', fontSize: '12px' }}
                          />
                        </div>
                      );
                    }
                    
                    // Handle primitive values (string, number, boolean)
                    return (
                      <div key={key} style={{ marginBottom: '15px' }}>
                        <label style={{ fontWeight: 600, color: '#495057', marginBottom: '4px', display: 'block' }}>
                          {formatKey(key)}:
                        </label>
                        <textarea
                          value={String(propValue)}
                          onChange={(e) => updateMetaobjectProperty(metaIndex, key, e.target.value)}
                          className="form-control"
                          rows={key === 'answer' || key === 'content' || key === 'description' ? 5 : 2}
                          style={{ fontSize: '14px' }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : (
          <textarea
            value={typeof value === 'string' 
              ? (hasHtmlTags(value) ? stripHtmlTags(value) : value)
              : (value || '')}
            onChange={(e) => setValue(e.target.value)}
            className="product-description-input form-control"
            rows={8}
          />
        )
      ) : (
        <div className="description-tabs">
          {metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0 ? (
            <>
              {renderMetaobjects()}
              {/* Show flattened_data if available */}
              {metafield.flattened_data && (
                <div style={{ 
                  marginTop: '20px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '5px',
                  border: '1px solid #dee2e6'
                }}>
                  <strong style={{ display: 'block', marginBottom: '10px', color: '#495057' }}>
                    Flattened Data:
                  </strong>
                  {metafield.flattened_data.fields && typeof metafield.flattened_data.fields === 'object' && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Fields:</strong>
                      {Object.entries(metafield.flattened_data.fields).map(([key, fieldValue]) => (
                        <div key={key} style={{ 
                          marginBottom: '8px', 
                          padding: '8px', 
                          background: '#ffffff', 
                          borderRadius: '4px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ fontWeight: 600, color: '#495057', marginBottom: '4px' }}>
                            {formatKey(key)}:
                          </div>
                          <div style={{ color: '#6c757d' }}>
                            {renderMetafieldValue(fieldValue)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.urls && Array.isArray(metafield.flattened_data.urls) && metafield.flattened_data.urls.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>URLs:</strong>
                      {metafield.flattened_data.urls.map((url, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                            {url}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.images && Array.isArray(metafield.flattened_data.images) && metafield.flattened_data.images.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Images:</strong>
                      {metafield.flattened_data.images.map((image, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          {renderMetafieldValue(image)}
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.files && Array.isArray(metafield.flattened_data.files) && metafield.flattened_data.files.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Files:</strong>
                      {metafield.flattened_data.files.map((file, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          {renderMetafieldValue(file)}
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.videos && Array.isArray(metafield.flattened_data.videos) && metafield.flattened_data.videos.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Videos:</strong>
                      {metafield.flattened_data.videos.map((video, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          {renderMetafieldValue(video)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {renderValue(value)}
              {metafield.flattened_data && (
                <div style={{ 
                  marginTop: '20px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '5px',
                  border: '1px solid #dee2e6'
                }}>
                  <strong style={{ display: 'block', marginBottom: '10px', color: '#495057' }}>
                    Flattened Data:
                  </strong>
                  {metafield.flattened_data.fields && typeof metafield.flattened_data.fields === 'object' && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Fields:</strong>
                      {Object.entries(metafield.flattened_data.fields).map(([key, fieldValue]) => (
                        <div key={key} style={{ 
                          marginBottom: '8px', 
                          padding: '8px', 
                          background: '#ffffff', 
                          borderRadius: '4px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ fontWeight: 600, color: '#495057', marginBottom: '4px' }}>
                            {formatKey(key)}:
                          </div>
                          <div style={{ color: '#6c757d' }}>
                            {renderMetafieldValue(fieldValue)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.urls && Array.isArray(metafield.flattened_data.urls) && metafield.flattened_data.urls.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>URLs:</strong>
                      {metafield.flattened_data.urls.map((url, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                            {url}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.images && Array.isArray(metafield.flattened_data.images) && metafield.flattened_data.images.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Images:</strong>
                      {metafield.flattened_data.images.map((image, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          {renderMetafieldValue(image)}
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.files && Array.isArray(metafield.flattened_data.files) && metafield.flattened_data.files.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Files:</strong>
                      {metafield.flattened_data.files.map((file, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          {renderMetafieldValue(file)}
                        </div>
                      ))}
                    </div>
                  )}
                  {metafield.flattened_data.videos && Array.isArray(metafield.flattened_data.videos) && metafield.flattened_data.videos.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#495057' }}>Videos:</strong>
                      {metafield.flattened_data.videos.map((video, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          {renderMetafieldValue(video)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MetafieldSection;
